import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  PageHeader,
  Table,
  Drawer,
  SearchBar,
  FilterBar,
  BadgePill,
  MetricTile,
  EmptyState,
} from '@/components/campus';
import {
  Plus,
  Calendar,
  MapPin,
  Users,
  Clock,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Loader2,
  MoreHorizontal,
  Ticket,
  ShieldCheck,
  UserMinus,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AIEventDescriptionGenerator } from '@/components/ai';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const statusColors = {
  draft: 'default',
  published: 'primary',
  live: 'success',
  completed: 'warning',
  cancelled: 'error',
};

// Format helpers
const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

const formatTimeForInput = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toTimeString().slice(0, 5);
};

// Initial form state
const initialFormState = {
  event_name: '',
  event_type: 'social',
  description: '',
  date: '',
  start_time: '',
  end_time: '',
  location: '',
  capacity: 100,
  visibility: 'public',
  ticketed: false,
  ticket_price: 0,
  requires_age_verification: false,
  min_age_requirement: 21,
  status: 'draft',
};

// Event types
const eventTypes = [
  { value: 'social', label: 'Social Event' },
  { value: 'party', label: 'Party' },
  { value: 'mixer', label: 'Mixer' },
  { value: 'formal', label: 'Formal' },
  { value: 'philanthropy', label: 'Philanthropy' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'other', label: 'Other' },
];

// Visibility options
const visibilityOptions = [
  { value: 'public', label: 'Public' },
  { value: 'members_only', label: 'Members Only' },
  { value: 'invite_only', label: 'Invite Only' },
];

// Status filter options
const statusFilterOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'live', label: 'Live' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

// ==================== EVENT FORM COMPONENT ====================
const EventForm = ({ event, onSubmit, onCancel, isLoading, organizationId }) => {
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (event) {
      setForm({
        event_name: event.event_name || '',
        event_type: event.event_type || 'social',
        description: event.description || '',
        date: formatDateForInput(event.date || event.start_time),
        start_time: formatTimeForInput(event.start_time),
        end_time: formatTimeForInput(event.end_time),
        location: event.location || '',
        capacity: event.capacity || 100,
        visibility: event.visibility || 'public',
        ticketed: event.ticketed || false,
        ticket_price: event.ticket_price || 0,
        requires_age_verification: event.requires_age_verification || false,
        min_age_requirement: event.min_age_requirement || 21,
        status: event.status || 'draft',
      });
    } else {
      setForm(initialFormState);
    }
  }, [event]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.event_name.trim()) newErrors.event_name = 'Event name is required';
    if (!form.date) newErrors.date = 'Date is required';
    if (!form.start_time) newErrors.start_time = 'Start time is required';
    if (!form.location.trim()) newErrors.location = 'Location is required';
    if (form.capacity < 1) newErrors.capacity = 'Capacity must be at least 1';
    if (form.ticketed && form.ticket_price < 0) newErrors.ticket_price = 'Price cannot be negative';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Combine date and time
    const startDateTime = new Date(`${form.date}T${form.start_time}`);
    const endDateTime = form.end_time ? new Date(`${form.date}T${form.end_time}`) : null;

    const payload = {
      event_name: form.event_name,
      event_type: form.event_type,
      description: form.description,
      date: form.date,
      start_time: startDateTime.toISOString(),
      end_time: endDateTime?.toISOString() || null,
      location: form.location,
      capacity: parseInt(form.capacity),
      visibility: form.visibility,
      ticketed: form.ticketed,
      ticket_price: form.ticketed ? parseFloat(form.ticket_price) : null,
      requires_age_verification: form.requires_age_verification,
      min_age_requirement: form.requires_age_verification ? parseInt(form.min_age_requirement) : null,
      status: form.status,
    };

    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Event Name */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Event Name *</Label>
        <Input
          value={form.event_name}
          onChange={(e) => handleChange('event_name', e.target.value)}
          placeholder="Fall Formal 2024"
          className="bg-obsidian border-white/[0.08] text-cloud-white"
          data-testid="event-name-input"
        />
        {errors.event_name && <p className="text-signal-red text-xs">{errors.event_name}</p>}
      </div>

      {/* Event Type */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Event Type</Label>
        <Select value={form.event_type} onValueChange={(v) => handleChange('event_type', v)}>
          <SelectTrigger className="bg-obsidian border-white/[0.08] text-cloud-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-graphite border-white/[0.08]">
            {eventTypes.map(type => (
              <SelectItem key={type.value} value={type.value} className="text-cloud-white hover:bg-white/[0.05]">
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Description</Label>
        <Textarea
          value={form.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Tell guests what to expect..."
          className="bg-obsidian border-white/[0.08] text-cloud-white min-h-[100px]"
          data-testid="event-description-input"
        />
        {/* AI Description Generator */}
        {organizationId && (
          <AIEventDescriptionGenerator
            organizationId={organizationId}
            eventName={form.event_name}
            eventType={form.event_type}
            date={form.date}
            startTime={form.start_time}
            location={form.location}
            onDescriptionGenerated={(desc) => handleChange('description', desc)}
          />
        )}
      </div>

      {/* Date & Time Row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label className="text-soft-silver">Date *</Label>
          <Input
            type="date"
            value={form.date}
            onChange={(e) => handleChange('date', e.target.value)}
            className="bg-obsidian border-white/[0.08] text-cloud-white"
            data-testid="event-date-input"
          />
          {errors.date && <p className="text-signal-red text-xs">{errors.date}</p>}
        </div>
        <div className="space-y-2">
          <Label className="text-soft-silver">Start Time *</Label>
          <Input
            type="time"
            value={form.start_time}
            onChange={(e) => handleChange('start_time', e.target.value)}
            className="bg-obsidian border-white/[0.08] text-cloud-white"
            data-testid="event-start-time-input"
          />
          {errors.start_time && <p className="text-signal-red text-xs">{errors.start_time}</p>}
        </div>
        <div className="space-y-2">
          <Label className="text-soft-silver">End Time</Label>
          <Input
            type="time"
            value={form.end_time}
            onChange={(e) => handleChange('end_time', e.target.value)}
            className="bg-obsidian border-white/[0.08] text-cloud-white"
            data-testid="event-end-time-input"
          />
        </div>
      </div>

      {/* Location */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Location *</Label>
        <Input
          value={form.location}
          onChange={(e) => handleChange('location', e.target.value)}
          placeholder="Chapter House, Main Ballroom"
          className="bg-obsidian border-white/[0.08] text-cloud-white"
          data-testid="event-location-input"
        />
        {errors.location && <p className="text-signal-red text-xs">{errors.location}</p>}
      </div>

      {/* Capacity & Visibility Row */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-soft-silver">Capacity *</Label>
          <Input
            type="number"
            min="1"
            value={form.capacity}
            onChange={(e) => handleChange('capacity', e.target.value)}
            className="bg-obsidian border-white/[0.08] text-cloud-white font-mono"
            data-testid="event-capacity-input"
          />
          {errors.capacity && <p className="text-signal-red text-xs">{errors.capacity}</p>}
        </div>
        <div className="space-y-2">
          <Label className="text-soft-silver">Visibility</Label>
          <Select value={form.visibility} onValueChange={(v) => handleChange('visibility', v)}>
            <SelectTrigger className="bg-obsidian border-white/[0.08] text-cloud-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-graphite border-white/[0.08]">
              {visibilityOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value} className="text-cloud-white hover:bg-white/[0.05]">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Ticketed Toggle */}
      <div className="flex items-center justify-between py-3 px-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
        <div>
          <p className="text-sm font-medium text-cloud-white">Ticketed Event</p>
          <p className="text-xs text-muted-text">Charge for entry</p>
        </div>
        <Switch
          checked={form.ticketed}
          onCheckedChange={(checked) => handleChange('ticketed', checked)}
          data-testid="event-ticketed-toggle"
        />
      </div>

      {/* Ticket Price (shown if ticketed) */}
      {form.ticketed && (
        <div className="space-y-2">
          <Label className="text-soft-silver">Ticket Price ($)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={form.ticket_price}
            onChange={(e) => handleChange('ticket_price', e.target.value)}
            className="bg-obsidian border-white/[0.08] text-cloud-white font-mono"
            data-testid="event-price-input"
          />
          {errors.ticket_price && <p className="text-signal-red text-xs">{errors.ticket_price}</p>}
        </div>
      )}

      {/* Age Verification Toggle */}
      <div className="flex items-center justify-between p-4 bg-signal-red/5 rounded-lg border border-signal-red/20">
        <div>
          <p className="text-sm font-medium text-cloud-white">Age Verification</p>
          <p className="text-xs text-muted-text">Require ID verification for entry</p>
        </div>
        <Switch
          checked={form.requires_age_verification}
          onCheckedChange={(checked) => handleChange('requires_age_verification', checked)}
          data-testid="event-age-verification-toggle"
        />
      </div>

      {/* Minimum Age (shown if age verification required) */}
      {form.requires_age_verification && (
        <div className="space-y-2">
          <Label className="text-soft-silver">Minimum Age</Label>
          <Input
            type="number"
            min="18"
            max="99"
            value={form.min_age_requirement}
            onChange={(e) => handleChange('min_age_requirement', e.target.value)}
            className="bg-obsidian border-white/[0.08] text-cloud-white font-mono"
            data-testid="event-min-age-input"
          />
        </div>
      )}

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t border-white/[0.06]">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1 border-white/[0.08] text-soft-silver hover:text-cloud-white"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
          disabled={isLoading}
          data-testid="event-submit-btn"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : event ? 'Update Event' : 'Create Event'}
        </Button>
      </div>
    </form>
  );
};

// ==================== DOOR STAFF ASSIGNMENT DRAWER ====================
const DoorStaffDrawer = ({ 
  event, 
  isOpen, 
  onClose,
  session,
  organization 
}) => {
  const [doorStaffUsers, setDoorStaffUsers] = useState([]);
  const [currentAssignments, setCurrentAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);

  // Fetch door staff users and current assignments
  useEffect(() => {
    const fetchData = async () => {
      if (!event?.id || !organization?.id || !isOpen) return;
      
      setLoading(true);
      
      try {
        // Fetch door staff users in org
        const usersRes = await fetch(
          `${API_URL}/api/door-staff/organization/${organization.id}/door-staff-users`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );
        
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setDoorStaffUsers(usersData.door_staff || []);
        }
        
        // Fetch current assignments for this event
        const assignRes = await fetch(
          `${API_URL}/api/door-staff/event/${event.id}/assignments`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );
        
        if (assignRes.ok) {
          const assignData = await assignRes.json();
          setCurrentAssignments(assignData.assignments || []);
        }
      } catch (error) {
        console.error('Failed to fetch door staff data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [event?.id, organization?.id, session?.access_token, isOpen]);

  const handleAssign = async (userId) => {
    setAssigning(userId);
    
    try {
      const res = await fetch(`${API_URL}/api/door-staff/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ user_id: userId, event_id: event.id }),
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Door staff assigned successfully');
        // Refresh assignments
        const assignRes = await fetch(
          `${API_URL}/api/door-staff/event/${event.id}/assignments`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );
        if (assignRes.ok) {
          const assignData = await assignRes.json();
          setCurrentAssignments(assignData.assignments || []);
        }
      } else {
        toast.error(data.message || 'Failed to assign door staff');
      }
    } catch (error) {
      console.error('Assign error:', error);
      toast.error('Failed to assign door staff');
    } finally {
      setAssigning(null);
    }
  };

  const handleRemove = async (assignmentId) => {
    try {
      const res = await fetch(`${API_URL}/api/door-staff/assignment/${assignmentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      
      if (res.ok) {
        toast.success('Assignment removed');
        setCurrentAssignments(prev => prev.filter(a => a.id !== assignmentId));
      } else {
        toast.error('Failed to remove assignment');
      }
    } catch (error) {
      console.error('Remove error:', error);
      toast.error('Failed to remove assignment');
    }
  };

  const isUserAssigned = (userId) => {
    return currentAssignments.some(a => a.user_id === userId);
  };

  if (!event) return null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Door Staff Assignment" width="lg">
      <div className="space-y-6">
        {/* Event Info */}
        <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
          <h3 className="font-medium text-cloud-white">{event.event_name}</h3>
          <p className="text-sm text-soft-silver mt-1">
            {formatDate(event.start_time)} at {formatTime(event.start_time)}
          </p>
          <p className="text-sm text-muted-text">{event.location}</p>
        </div>

        {/* Current Assignments */}
        <div>
          <h4 className="text-sm font-medium text-soft-silver mb-3 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            Assigned Door Staff ({currentAssignments.length})
          </h4>
          
          {currentAssignments.length === 0 ? (
            <p className="text-sm text-muted-text italic">No door staff assigned yet</p>
          ) : (
            <div className="space-y-2">
              {currentAssignments.map((assignment) => {
                const profile = assignment.profiles || {};
                return (
                  <div 
                    key={assignment.id}
                    className="flex items-center justify-between p-3 bg-cool-mint/5 rounded-lg border border-cool-mint/20"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-cool-mint/20 flex items-center justify-center">
                        <ShieldCheck className="w-4 h-4 text-cool-mint" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-cloud-white">
                          {profile.display_name || 'Unknown'}
                        </p>
                        <p className="text-xs text-soft-silver">{profile.email}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(assignment.id)}
                      className="text-signal-red hover:bg-signal-red/10"
                      data-testid={`remove-assignment-${assignment.id}`}
                    >
                      <UserMinus className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Available Door Staff */}
        <div>
          <h4 className="text-sm font-medium text-soft-silver mb-3">
            Available Door Staff
          </h4>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-electric-ice-blue animate-spin" />
            </div>
          ) : doorStaffUsers.length === 0 ? (
            <div className="text-center py-8 text-muted-text">
              <p className="text-sm">No users with door_staff role found</p>
              <p className="text-xs mt-1">Assign the door_staff role to members first</p>
            </div>
          ) : (
            <div className="space-y-2">
              {doorStaffUsers.map((staff) => {
                const assigned = isUserAssigned(staff.user_id);
                return (
                  <div 
                    key={staff.user_id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      assigned 
                        ? 'bg-white/[0.02] border-white/[0.04] opacity-50' 
                        : 'bg-graphite/50 border-white/[0.06]'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {staff.avatar_url ? (
                        <img 
                          src={staff.avatar_url} 
                          alt={staff.display_name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-electric-ice-blue/20 flex items-center justify-center">
                          <Users className="w-4 h-4 text-electric-ice-blue" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-cloud-white">
                          {staff.display_name}
                        </p>
                        <p className="text-xs text-soft-silver">{staff.email}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      disabled={assigned || assigning === staff.user_id}
                      onClick={() => handleAssign(staff.user_id)}
                      className={`${
                        assigned 
                          ? 'bg-white/[0.05] text-muted-text cursor-not-allowed'
                          : 'bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90'
                      }`}
                      data-testid={`assign-staff-${staff.user_id}`}
                    >
                      {assigning === staff.user_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : assigned ? (
                        'Assigned'
                      ) : (
                        'Assign'
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

// ==================== EVENT DETAIL DRAWER ====================
const EventDetailDrawer = ({ 
  event, 
  isOpen, 
  onClose, 
  onEdit, 
  onStatusChange, 
  onViewGuests, 
  onDelete,
  onRsvp,
  isRsvping,
  userHasTicket,
  canManageEvents,
  onManageDoorStaff
}) => {
  if (!event) return null;

  const stats = [
    { label: 'Total Guests', value: event.guest_count || 0 },
    { label: 'Checked In', value: event.checked_in_count || 0 },
    { label: 'Pending', value: (event.guest_count || 0) - (event.checked_in_count || 0) },
  ];

  const capacityPercent = event.capacity > 0 
    ? Math.round(((event.guest_count || 0) / event.capacity) * 100) 
    : 0;

  const isEventFull = event.guest_count >= event.capacity;
  const canRsvp = event.status === 'published' || event.status === 'live';

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={event.event_name} width="lg">
      <div className="space-y-6">
        {/* Status & Type */}
        <div className="flex items-center gap-3">
          <BadgePill variant={statusColors[event.status] || 'default'}>
            {event.status?.toUpperCase()}
          </BadgePill>
          <span className="text-sm text-muted-text capitalize">{event.event_type}</span>
        </div>

        {/* RSVP Button - Show for members who haven't RSVP'd */}
        {!canManageEvents && canRsvp && !userHasTicket && (
          <div className="p-4 bg-electric-ice-blue/5 rounded-lg border border-electric-ice-blue/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-cloud-white">Want to attend?</p>
                <p className="text-sm text-soft-silver">RSVP to get your ticket and QR code</p>
              </div>
              <Button
                onClick={() => onRsvp(event.id)}
                disabled={isRsvping || isEventFull}
                className="bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
                data-testid="rsvp-btn"
              >
                {isRsvping ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    RSVPing...
                  </>
                ) : isEventFull ? (
                  'Event Full'
                ) : (
                  <>
                    <Ticket className="w-4 h-4 mr-2" />
                    RSVP Now
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Already RSVP'd message */}
        {userHasTicket && (
          <div className="p-4 bg-cool-mint/10 rounded-lg border border-cool-mint/20">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-cool-mint" />
              <div>
                <p className="font-medium text-cool-mint">You're going!</p>
                <p className="text-sm text-soft-silver">Check your ticket in "My Tickets"</p>
              </div>
            </div>
          </div>
        )}

        {/* Event Details */}
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-electric-ice-blue mt-0.5" />
            <div>
              <p className="text-cloud-white">{formatDate(event.start_time)}</p>
              <p className="text-sm text-muted-text font-mono">
                {formatTime(event.start_time)}
                {event.end_time && ` - ${formatTime(event.end_time)}`}
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-electric-ice-blue mt-0.5" />
            <p className="text-cloud-white">{event.location}</p>
          </div>
          
          <div className="flex items-start gap-3">
            <Eye className="w-5 h-5 text-electric-ice-blue mt-0.5" />
            <p className="text-cloud-white capitalize">{event.visibility?.replace('_', ' ')}</p>
          </div>
        </div>

        {/* Description */}
        {event.description && (
          <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
            <p className="text-soft-silver text-sm">{event.description}</p>
          </div>
        )}

        {/* Capacity Overview */}
        <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-soft-silver">Capacity</span>
            <span className="text-sm font-mono text-cloud-white">
              {event.guest_count || 0} / {event.capacity}
            </span>
          </div>
          <div className="h-2 bg-white/[0.08] rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all ${
                capacityPercent >= 90 ? 'bg-signal-red' : 
                capacityPercent >= 70 ? 'bg-soft-amber' : 
                'bg-cool-mint'
              }`}
              style={{ width: `${Math.min(capacityPercent, 100)}%` }}
            />
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          {stats.map((stat, i) => (
            <div key={i} className="text-center p-3 bg-obsidian/50 rounded-lg border border-white/[0.06]">
              <p className="text-2xl font-mono font-semibold text-cloud-white">{stat.value}</p>
              <p className="text-xs text-muted-text">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Ticketing Info */}
        {event.ticketed && (
          <div className="flex items-center justify-between p-4 bg-soft-amber/10 rounded-lg border border-soft-amber/20">
            <span className="text-sm text-soft-amber">Ticketed Event</span>
            <span className="font-mono font-semibold text-soft-amber">${event.ticket_price}</span>
          </div>
        )}

        {/* Age Verification Info */}
        {event.requires_age_verification && (
          <div className="flex items-center justify-between p-4 bg-signal-red/10 rounded-lg border border-signal-red/20">
            <span className="text-sm text-signal-red">Age Verification Required</span>
            <span className="font-mono font-semibold text-signal-red">{event.min_age_requirement || 21}+</span>
          </div>
        )}

        {/* Admin Actions - Only show for managers */}
        {canManageEvents && (
          <div className="space-y-3 pt-4 border-t border-white/[0.06]">
            <Button
              onClick={() => onViewGuests(event)}
              className="w-full bg-electric-ice-blue/10 text-electric-ice-blue border border-electric-ice-blue/20 hover:bg-electric-ice-blue/20"
              data-testid="view-guests-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              View Guest List
            </Button>
            
            {/* Door Staff Assignment Button */}
            <Button
              onClick={() => onManageDoorStaff(event)}
              variant="outline"
              className="w-full border-cool-mint/30 text-cool-mint hover:bg-cool-mint/10"
              data-testid="manage-door-staff-btn"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              Manage Door Staff
            </Button>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => onEdit(event)}
                variant="outline"
                className="border-white/[0.08] text-soft-silver hover:text-cloud-white"
                data-testid="edit-event-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-white/[0.08] text-soft-silver hover:text-cloud-white"
                    data-testid="change-status-btn"
                  >
                    Change Status
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-graphite border-white/[0.08]">
                  {event.status === 'draft' && (
                    <DropdownMenuItem 
                      onClick={() => onStatusChange(event.id, 'published')}
                      className="text-electric-ice-blue hover:bg-white/[0.05]"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Publish
                    </DropdownMenuItem>
                  )}
                  {event.status === 'published' && (
                    <DropdownMenuItem 
                      onClick={() => onStatusChange(event.id, 'live')}
                      className="text-cool-mint hover:bg-white/[0.05]"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Go Live
                    </DropdownMenuItem>
                  )}
                  {(event.status === 'live' || event.status === 'published') && (
                    <DropdownMenuItem 
                      onClick={() => onStatusChange(event.id, 'completed')}
                      className="text-soft-amber hover:bg-white/[0.05]"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark Complete
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/[0.06]" />
                  {event.status !== 'cancelled' && (
                    <DropdownMenuItem 
                      onClick={() => onStatusChange(event.id, 'cancelled')}
                      className="text-signal-red hover:bg-white/[0.05]"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancel Event
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {/* Delete Button */}
            <Button
              onClick={() => onDelete(event.id)}
              variant="outline"
              className="w-full border-signal-red/30 text-signal-red hover:bg-signal-red/10"
              data-testid="delete-event-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Event
            </Button>
          </div>
        )}
      </div>
    </Drawer>
  );
};

// ==================== MAIN EVENTS PAGE ====================
export const Events = () => {
  const { organization, session, orgRole } = useAuth();
  const navigate = useNavigate();

  // State
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pageSize: 20 });
  
  // RSVP state
  const [isRsvping, setIsRsvping] = useState(false);
  const [userTickets, setUserTickets] = useState([]); // Track user's existing tickets
  
  // Door Staff state
  const [isDoorStaffOpen, setIsDoorStaffOpen] = useState(false);
  const [doorStaffEvent, setDoorStaffEvent] = useState(null);

  // Permission check
  const canManageEvents = ['owner', 'admin', 'event_manager'].includes(orgRole);

  // Handle manage door staff
  const handleManageDoorStaff = (event) => {
    setDoorStaffEvent(event);
    setIsDoorStaffOpen(true);
  };

  // Fetch user's existing tickets
  const fetchUserTickets = useCallback(async () => {
    if (!session?.access_token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/tickets/my-tickets`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        setUserTickets(data.tickets || []);
      }
    } catch (error) {
      console.error('Failed to fetch user tickets:', error);
    }
  }, [session?.access_token]);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    if (!organization?.id || !session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const params = new URLSearchParams({
        organization_id: organization.id,
        page: pagination.page.toString(),
        page_size: pagination.pageSize.toString(),
      });
      
      if (statusFilter) params.append('status', statusFilter);

      const res = await fetch(`${API_URL}/api/events?${params}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
        setPagination(prev => ({ ...prev, total: data.total || 0 }));
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token, statusFilter, pagination.page, pagination.pageSize]);

  useEffect(() => {
    fetchEvents();
    fetchUserTickets();
  }, [fetchEvents, fetchUserTickets]);

  // Check if user has ticket for a specific event
  const userHasTicketForEvent = (eventId) => {
    return userTickets.some(ticket => ticket.event_id === eventId);
  };

  // RSVP to event - Enhanced flow with payment & age verification
  const handleRsvp = async (eventId) => {
    if (!session?.access_token) return;
    setIsRsvping(true);

    try {
      // First check event requirements
      const reqRes = await fetch(`${API_URL}/api/tickets/event/${eventId}/requirements`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      
      const requirements = await reqRes.json();
      
      // If event is full, show error
      if (requirements.is_full) {
        toast.error('Event is at full capacity');
        setIsRsvping(false);
        return;
      }
      
      // Start RSVP with success/cancel URLs
      const baseUrl = window.location.origin;
      const res = await fetch(`${API_URL}/api/tickets/rsvp/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ 
          event_id: eventId,
          success_url: `${baseUrl}/app/events?payment=success`,
          cancel_url: `${baseUrl}/app/events?payment=cancelled`,
        }),
      });
      
      const data = await res.json();
      
      if (!data.success) {
        toast.error(data.error || 'Failed to RSVP');
        setIsRsvping(false);
        return;
      }
      
      // Handle next step based on response
      if (data.next_step === 'payment') {
        // Redirect to Stripe checkout
        toast.info('Redirecting to payment...');
        window.location.href = data.checkout_url;
        return;
      }
      
      if (data.next_step === 'age_verification') {
        // Store attendee_id and redirect to age verification
        sessionStorage.setItem('pending_rsvp_attendee_id', data.attendee_id);
        sessionStorage.setItem('pending_rsvp_event_id', eventId);
        toast.info('Age verification required. Redirecting...');
        navigate(`/verify/age?attendee_id=${data.attendee_id}&event_id=${eventId}`);
        return;
      }
      
      if (data.next_step === 'complete') {
        // Direct success - no payment or verification required
        toast.success('RSVP successful! Check "My Tickets" for your QR code.');
        setIsDetailOpen(false);
        fetchUserTickets();
        fetchEvents();
        
        if (data.pass_id) {
          navigate(`/app/my-ticket/${data.pass_id}`);
        }
      }
    } catch (error) {
      console.error('RSVP error:', error);
      toast.error('Failed to RSVP to event');
    } finally {
      setIsRsvping(false);
    }
  };

  // Handle payment success/cancel redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    const sessionId = params.get('session_id');
    const attendeeId = params.get('attendee_id');
    
    if (paymentStatus === 'cancelled') {
      toast.error('Payment was cancelled');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    
    if (paymentStatus === 'success' && sessionId && attendeeId && session?.access_token) {
      // Confirm payment and complete RSVP
      const confirmPayment = async () => {
        try {
          const res = await fetch(`${API_URL}/api/tickets/rsvp/confirm-payment`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ session_id: sessionId, attendee_id: attendeeId }),
          });
          
          const data = await res.json();
          
          if (data.success) {
            if (data.next_step === 'age_verification') {
              toast.info('Payment confirmed! Age verification required.');
              navigate(`/verify/age?attendee_id=${attendeeId}`);
            } else if (data.next_step === 'complete') {
              toast.success('Payment confirmed! Your ticket is ready.');
              if (data.pass_id) {
                navigate(`/app/my-ticket/${data.pass_id}`);
              }
            }
          } else {
            toast.error(data.error || 'Payment confirmation failed');
          }
        } catch (error) {
          console.error('Payment confirmation error:', error);
        }
        
        window.history.replaceState({}, '', window.location.pathname);
      };
      
      confirmPayment();
    }
  }, [session?.access_token, navigate]);

  // Filter events by search
  const filteredEvents = events.filter(event => 
    event.event_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    event.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Create event
  const handleCreateEvent = async (data) => {
    if (!organization?.id) return;
    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/events?organization_id=${organization.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(data),
      });

      if (res.ok) {
        toast.success('Event created successfully');
        setIsFormOpen(false);
        fetchEvents();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create event');
      }
    } catch (error) {
      toast.error('Failed to create event');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update event
  const handleUpdateEvent = async (data) => {
    if (!editingEvent?.id) return;
    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/events/${editingEvent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(data),
      });

      if (res.ok) {
        toast.success('Event updated successfully');
        setIsFormOpen(false);
        setEditingEvent(null);
        fetchEvents();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update event');
      }
    } catch (error) {
      toast.error('Failed to update event');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Change event status
  const handleStatusChange = async (eventId, newStatus) => {
    try {
      const endpoint = newStatus === 'published' 
        ? `${API_URL}/api/events/${eventId}/publish`
        : newStatus === 'cancelled'
        ? `${API_URL}/api/events/${eventId}/cancel`
        : `${API_URL}/api/events/${eventId}`;

      const method = (newStatus === 'published' || newStatus === 'cancelled') ? 'POST' : 'PUT';
      const body = (newStatus === 'published' || newStatus === 'cancelled') 
        ? null 
        : JSON.stringify({ status: newStatus });

      const res = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body,
      });

      if (res.ok) {
        toast.success(`Event ${newStatus}`);
        setIsDetailOpen(false);
        fetchEvents();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Open event detail
  const handleViewEvent = (event) => {
    setSelectedEvent(event);
    setIsDetailOpen(true);
  };

  // Open edit form
  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setIsDetailOpen(false);
    setIsFormOpen(true);
  };

  // View guests
  const handleViewGuests = (event) => {
    navigate(`/app/guests?event_id=${event.id}`);
  };

  // Delete event
  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (res.ok) {
        toast.success('Event deleted successfully');
        setIsDetailOpen(false);
        setSelectedEvent(null);
        fetchEvents();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete event');
      }
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  // Table columns
  const columns = [
    {
      key: 'event_name',
      label: 'Event',
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-electric-ice-blue/10 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-electric-ice-blue" />
          </div>
          <div>
            <p className="font-medium text-cloud-white">{row.event_name}</p>
            <p className="text-xs text-muted-text capitalize">{row.event_type}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'start_time',
      label: 'Date & Time',
      mono: true,
      render: (_, row) => (
        <div>
          <p className="text-cloud-white">{formatDate(row.start_time)}</p>
          <p className="text-xs text-muted-text">{formatTime(row.start_time)}</p>
        </div>
      ),
    },
    {
      key: 'location',
      label: 'Location',
      render: (value) => (
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-muted-text" />
          <span className="text-soft-silver">{value}</span>
        </div>
      ),
    },
    {
      key: 'capacity',
      label: 'Capacity',
      align: 'center',
      mono: true,
      render: (_, row) => (
        <span className="text-soft-silver">
          {row.guest_count || 0} / {row.capacity}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      align: 'center',
      render: (value) => (
        <BadgePill variant={statusColors[value] || 'default'}>
          {value?.toUpperCase()}
        </BadgePill>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      sortable: false,
      render: (_, row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="w-4 h-4 text-muted-text" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-graphite border-white/[0.08]">
            <DropdownMenuItem 
              onClick={() => handleViewEvent(row)}
              className="text-cloud-white hover:bg-white/[0.05]"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => handleEditEvent(row)}
              className="text-cloud-white hover:bg-white/[0.05]"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => handleViewGuests(row)}
              className="text-cloud-white hover:bg-white/[0.05]"
            >
              <Users className="w-4 h-4 mr-2" />
              View Guests
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/[0.06]" />
            <DropdownMenuItem 
              onClick={() => handleDeleteEvent(row.id)}
              className="text-signal-red hover:bg-signal-red/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Event
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  // Filter configuration
  const filters = [
    {
      key: 'status',
      label: 'Status',
      options: statusFilterOptions,
    },
  ];

  const activeFilters = statusFilter 
    ? [{ key: 'status', label: 'Status', value: statusFilter }]
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="events-page">
      {/* Page Header */}
      <PageHeader
        title="Events"
        subtitle={`${pagination.total} events`}
        actions={
          <Button
            onClick={() => { setEditingEvent(null); setIsFormOpen(true); }}
            className="bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
            data-testid="create-event-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Event
          </Button>
        }
      />

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <SearchBar
            placeholder="Search events..."
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </div>
        <FilterBar
          filters={filters}
          activeFilters={activeFilters}
          onFilterChange={(key, value) => {
            if (key === 'status') setStatusFilter(value || '');
          }}
          onClearAll={() => setStatusFilter('')}
        />
      </div>

      {/* Events Table */}
      <Table
        columns={columns}
        data={filteredEvents}
        emptyState={
          <div className="py-12">
            <EmptyState
              icon="events"
              title="No events yet"
              description="Create your first event to get started with guest management."
              actionLabel="Create Event"
              onAction={() => { setEditingEvent(null); setIsFormOpen(true); }}
            />
          </div>
        }
      />

      {/* Create/Edit Event Drawer */}
      <Drawer
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingEvent(null); }}
        title={editingEvent ? 'Edit Event' : 'Create Event'}
        width="lg"
      >
        <EventForm
          event={editingEvent}
          onSubmit={editingEvent ? handleUpdateEvent : handleCreateEvent}
          onCancel={() => { setIsFormOpen(false); setEditingEvent(null); }}
          isLoading={isSubmitting}
          organizationId={organization?.id}
        />
      </Drawer>

      {/* Event Detail Drawer */}
      <EventDetailDrawer
        event={selectedEvent}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        onEdit={handleEditEvent}
        onStatusChange={handleStatusChange}
        onViewGuests={handleViewGuests}
        onDelete={handleDeleteEvent}
        onRsvp={handleRsvp}
        isRsvping={isRsvping}
        userHasTicket={selectedEvent ? userHasTicketForEvent(selectedEvent.id) : false}
        canManageEvents={canManageEvents}
        onManageDoorStaff={handleManageDoorStaff}
      />

      {/* Door Staff Assignment Drawer */}
      <DoorStaffDrawer
        event={doorStaffEvent}
        isOpen={isDoorStaffOpen}
        onClose={() => {
          setIsDoorStaffOpen(false);
          setDoorStaffEvent(null);
        }}
        session={session}
        organization={organization}
      />
    </div>
  );
};

export default Events;
